import { type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, X } from 'lucide-react';
import type {
  Source,
  ActionConfig,
  ActionType,
  KeywordFilterConfig,
  AIFilterConfig,
  SummarizeConfig,
  TranslateConfig,
} from '@new-signal/shared';
import { KeywordFilterCard } from '../KeywordFilterCard';
import { AIFilterCard } from '../AIFilterCard';
import { SummarizeCard } from '../SummarizeCard';
import { TranslateCard } from '../TranslateCard';
import { AddStepMenu } from '../AddStepMenu';
import { cn } from '../../lib/utils';

const sectionLabelClass =
  'm-0 mb-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-brand-muted';

const inputClass =
  'box-border w-full rounded-lg border border-brand-border-medium bg-white px-2.5 py-[7px] text-[13px] leading-5 text-brand-text outline-none transition-[border-color,box-shadow] focus:border-accent-blue focus:ring-[3px] focus:ring-accent-blue/[0.12]';

const addButtonClass =
  'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-accent-blue px-3.5 text-[13px] font-medium text-white transition-colors hover:bg-accent-blue-hover disabled:cursor-not-allowed disabled:bg-[#B0B0B5]';

const primaryButtonClass =
  'inline-flex cursor-pointer items-center justify-center rounded-full border-none bg-accent-blue px-4 py-1.5 text-[12.5px] font-medium leading-5 text-white transition-colors hover:bg-accent-blue-hover disabled:cursor-not-allowed disabled:bg-[#B0B0B5]';

const ghostButtonClass =
  'inline-flex cursor-pointer items-center justify-center rounded-full border-none bg-transparent px-2.5 py-1.5 text-[12.5px] font-medium leading-5 text-brand-muted transition-colors hover:text-brand-text disabled:cursor-not-allowed disabled:opacity-50';

function shortenUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.hostname.replace(/^www\./, '')}${parsed.pathname === '/' ? '' : parsed.pathname}`;
  } catch {
    return url;
  }
}

export interface FeedConfigPanelProps {
  sources: Source[];
  actions: ActionConfig[];
  actionsDirty: boolean;
  savingActions: boolean;
  actionValidationErrors: Record<number, string>;
  actionsError: string | null;
  adding: boolean;
  addError: string | null;
  url: string;
  onUrlChange: (value: string) => void;
  onAddSource: (e: FormEvent) => void;
  onRemoveSource: (id: string) => void;
  onAddAction: (type: ActionType) => void;
  onUpdateAction: (index: number, updated: ActionConfig) => void;
  onRemoveAction: (index: number) => void;
  onSaveActions: () => void;
  onDiscardChanges: () => void;
}

export function FeedConfigPanel(props: FeedConfigPanelProps) {
  const { t } = useTranslation('feed-builder');
  const { t: tc } = useTranslation('common');

  return (
    <aside
      aria-label={t('reader.config.title')}
      className="flex h-full w-[280px] shrink-0 flex-col border-r border-brand-border bg-brand-surface"
    >
      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-4 pt-5">
        {/* Sources */}
        <section className="mb-[22px]">
          <h3 className={sectionLabelClass}>{t('detail.sources')}</h3>

          <form onSubmit={props.onAddSource} className="mb-2.5 flex gap-1.5">
            <input
              type="text"
              value={props.url}
              onChange={(e) => props.onUrlChange(e.target.value)}
              placeholder={t('detail.addSourcePlaceholder')}
              className={cn(
                inputClass,
                'flex-1',
                props.addError &&
                  'border-brand-error focus:border-brand-error focus:ring-brand-error/10',
              )}
            />
            <button
              type="submit"
              disabled={props.adding || !props.url.trim()}
              className={addButtonClass}
            >
              {props.adding ? t('detail.adding') : tc('add')}
            </button>
          </form>

          {props.addError && (
            <p className="mb-2.5 text-[12px] leading-4 text-brand-error">{props.addError}</p>
          )}

          {props.sources.length === 0 ? (
            <p className="rounded-[10px] border border-dashed border-brand-border-medium bg-white px-3 py-3 text-center text-[12px] leading-4 text-brand-muted">
              {t('detail.noSources')}
            </p>
          ) : (
            <ul className="divide-y divide-brand-border overflow-hidden rounded-[10px] border border-brand-border bg-white">
              {props.sources.map((source) => (
                <li
                  key={source.id}
                  className="flex items-center gap-2 px-2.5 py-[7px] transition-colors hover:bg-black/[0.02]"
                >
                  <Globe
                    size={12}
                    strokeWidth={1.75}
                    className="shrink-0 text-brand-muted"
                    aria-hidden
                  />
                  <span
                    className="min-w-0 flex-1 truncate text-[12.5px] text-brand-text"
                    title={source.url}
                  >
                    {shortenUrl(source.url)}
                  </span>
                  <button
                    type="button"
                    onClick={() => props.onRemoveSource(source.id)}
                    aria-label={t('detail.removeSource')}
                    title={t('detail.removeSource')}
                    className="flex shrink-0 cursor-pointer border-none bg-transparent p-0.5 text-brand-muted transition-colors hover:text-brand-error"
                  >
                    <X size={12} strokeWidth={2} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Actions */}
        <section>
          <h3 className={sectionLabelClass}>{t('detail.actions')}</h3>
          <div className="flex flex-col gap-2.5">
            {props.actions.map((action, index) => {
              if (action.type === 'keyword_filter') {
                return (
                  <KeywordFilterCard
                    key={index}
                    config={action as KeywordFilterConfig}
                    onChange={(updated) => props.onUpdateAction(index, updated)}
                    onRemove={() => props.onRemoveAction(index)}
                    error={props.actionValidationErrors[index] ?? null}
                  />
                );
              }
              if (action.type === 'ai_filter') {
                return (
                  <AIFilterCard
                    key={index}
                    config={action as AIFilterConfig}
                    onChange={(updated) => props.onUpdateAction(index, updated)}
                    onRemove={() => props.onRemoveAction(index)}
                    error={props.actionValidationErrors[index] ?? null}
                  />
                );
              }
              if (action.type === 'summarize') {
                return (
                  <SummarizeCard
                    key={index}
                    config={action as SummarizeConfig}
                    onChange={(updated) => props.onUpdateAction(index, updated)}
                    onRemove={() => props.onRemoveAction(index)}
                  />
                );
              }
              if (action.type === 'translate') {
                return (
                  <TranslateCard
                    key={index}
                    config={action as TranslateConfig}
                    onChange={(updated) => props.onUpdateAction(index, updated)}
                    onRemove={() => props.onRemoveAction(index)}
                    error={props.actionValidationErrors[index] ?? null}
                  />
                );
              }
              return null;
            })}
            <AddStepMenu onAdd={props.onAddAction} />
          </div>
          {props.actionsError && (
            <p
              className="mt-3 rounded-lg border border-accent-error-border bg-accent-error-bg px-3 py-2 text-[12px] leading-4 text-accent-error-text"
              role="alert"
            >
              {props.actionsError}
            </p>
          )}
        </section>
      </div>

      {/* Sticky unsaved changes bar */}
      {props.actionsDirty && (
        <div className="border-t border-brand-border bg-white/90 px-3.5 py-2.5 backdrop-blur-[10px]">
          <p className="mb-2 text-[12px] font-medium leading-4 text-brand-text">
            {t('detail.unsavedChanges')}
          </p>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={props.onDiscardChanges}
              disabled={props.savingActions}
              className={ghostButtonClass}
            >
              {t('detail.discardChanges')}
            </button>
            <button
              type="button"
              onClick={props.onSaveActions}
              disabled={props.savingActions}
              className={primaryButtonClass}
            >
              {props.savingActions ? tc('saving') : t('detail.saveActions')}
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}
